import React, { useMemo, useState, useCallback } from "react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

export default function UniversalTable({
  data = [],
  columns = [],
  emptyText = "-",
  getRowKey,
  tableClassName = "",
  containerClassName = "",
  rowClassName,
  onRowClick,
  tableProps,
  containerProps,
}) {
  const safeColumns = useMemo(() => Array.isArray(columns) ? columns : [], [columns]);
  const rows = useMemo(() => Array.isArray(data) ? data : [], [data]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Daha güvenli row key çözümleyici
  const resolveRowKey = useCallback((row, index) => {
    if (typeof getRowKey === "function") {
      const customKey = getRowKey(row, index);
      return customKey !== undefined && customKey !== null ? String(customKey) : `row-${index}`;
    }

    if (row && row.id !== undefined && row.id !== null) {
      return String(row.id);
    }

    // Object'in JSON string'ini kullanarak daha benzersiz key oluştur
    try {
      const jsonString = JSON.stringify(row);
      return `row-${index}-${btoa(encodeURIComponent(jsonString)).slice(0, 20)}`;
    } catch (e) {
      return `row-${index}`;
    }
  }, [getRowKey]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;

    return [...rows].sort((a, b) => {
      const { key: sortKey, direction } = sortConfig;

      // Sütunun render fonksiyonunu bul
      const column = safeColumns.find(col => col.key === sortKey);
      if (!column) return 0;

      // Render edilen değeri al
      const getValue = (row) => {
        if (typeof column.render === "function") {
          return column.render(row);
        }
        return row?.[column.key];
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      // String karşılaştırması
      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return direction === "asc" ? comparison : -comparison;
      }

      // Sayısal karşılaştırma
      if (typeof aValue === "number" && typeof bValue === "number") {
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Boolean karşılaştırma
      if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        return direction === "asc" ? (aValue === bValue ? 0 : aValue ? -1 : 1) : (aValue === bValue ? 0 : aValue ? 1 : -1);
      }

      // Null/undefined kontrolü
      if (aValue == null && bValue != null) return direction === "asc" ? -1 : 1;
      if (aValue != null && bValue == null) return direction === "asc" ? 1 : -1;
      if (aValue == null && bValue == null) return 0;

      // Diğer türler için genel karşılaştırma
      const aStr = String(aValue);
      const bStr = String(bValue);
      const comparison = aStr.localeCompare(bStr);
      return direction === "asc" ? comparison : -comparison;
    });
  }, [rows, sortConfig, safeColumns]);

  const colCount = safeColumns.length || 1;

  const handleRowClick = (row, i) => {
    if (typeof onRowClick === "function") onRowClick(row, i);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort style={{ opacity: 0.5, fontSize: "0.8em", marginLeft: "4px" }} />;
    }

    if (sortConfig.direction === "asc") {
      return <FaSortUp style={{ fontSize: "0.8em", marginLeft: "4px" }} />;
    }

    return <FaSortDown style={{ fontSize: "0.8em", marginLeft: "4px" }} />;
  };

  return (
    <div className={containerClassName} {...(containerProps || {})}>
      <table className={tableClassName} {...(tableProps || {})}>
        {safeColumns.length > 0 && (
          <thead>
            <tr>
              {safeColumns.map((col, i) => {
                const hasHeader = col.header && col.header.trim() !== "";
                const isSortable = hasHeader && col.sortable !== false;
                return (
                  <th
                    key={`header-${i}`}
                    className={col.headerClassName}
                    style={{
                      ...(col.width ? { width: col.width } : {}),
                      ...(isSortable ? { cursor: "var(--pointer)" } : {}),
                    }}
                    onClick={() => isSortable && handleSort(col.key)}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
                      {col.header ?? ""}
                      {isSortable && getSortIcon(col.key)}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
        )}

        <tbody>
          {sortedRows.length > 0 ? (
            sortedRows.map((row, rowIndex) => (
              <tr
                key={resolveRowKey(row, rowIndex)}
                className={typeof rowClassName === "function" ? rowClassName(row, rowIndex) : undefined}
                onClick={() => handleRowClick(row, rowIndex)}
                style={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {safeColumns.map((col, colIndex) => {
                  const content = typeof col.render === "function" ? col.render(row, rowIndex) : row?.[col.key];
                  const textAlign = col.align ? { textAlign: col.align } : undefined;
                  const widthStyle = col.width ? { width: col.width } : undefined;
                  const style = (textAlign || widthStyle) ? { ...textAlign, ...widthStyle } : undefined;

                  return (
                    <td
                      key={`cell-${rowIndex}-${colIndex}`}
                      className={col.className}
                      style={style}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={colCount} style={{ textAlign: "center" }}>{emptyText}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}